<template>
    <div class="user-view-container">
        <div v-if="userdata" class="modern-user-card">
            <!-- View Mode -->
            <div v-if="!edit">
                <div class="user-header">
                    <div class="user-avatar-section">
                        <div class="avatar-wrapper" @click="showFullImage">
                            <div v-if="getUserImage()" class="avatar-image-container">
                                <img
                                    :src="getUserImage()"
                                    :alt="userdata.fullname"
                                    class="user-avatar-image"
                                    @error="handleImageError"
                                />
                                <div class="avatar-overlay">
                                    <b-icon icon="zoom-in"></b-icon>
                                </div>
                            </div>
                            <div v-else class="avatar-placeholder">
                                {{ getInitials(userdata.fullname || userdata.username) }}
                                <div class="no-image-text">No Image</div>
                            </div>
                        </div>
                        <b-button
                            v-if="isAdmin"
                            @click="openImageUpload"
                            variant="outline-info"
                            size="sm"
                            class="upload-btn mt-2"
                            title="Upload/Change Image"
                        >
                            <b-icon icon="camera" class="mr-1"></b-icon>
                            Change Image
                        </b-button>
                    </div>

                    <div class="user-info-section">
                        <h3 class="user-name">{{ userdata.fullname || 'No Name' }}</h3>
                        <div class="user-username">@{{ userdata.username }}</div>
                        <div class="user-role-badge" :class="getRoleClass(userdata.userrole)">
                            {{ getUserRoleLabel(userdata.userrole) }}
                        </div>
                        <div class="user-status">
                            <span class="status-dot" :class="{ 'active': userdata.active }"></span>
                            {{ userdata.active ? 'Active' : 'Inactive' }}
                        </div>
                    </div>
                </div>

                <div class="user-details-section">
                    <!-- User Information -->
                    <div class="detail-card">
                        <h5 class="detail-title">
                            <b-icon icon="info-circle" class="mr-2"></b-icon>
                            User Information
                        </h5>
                        <div class="detail-grid">
                            <div class="detail-item">
                                <label class="detail-label">Full Name</label>
                                <div class="detail-value">{{ userdata.fullname || 'Not set' }}</div>
                            </div>

                            <div class="detail-item">
                                <label class="detail-label">Username</label>
                                <div class="detail-value">{{ userdata.username }}</div>
                            </div>

                            <div class="detail-item">
                                <label class="detail-label">User Role</label>
                                <div class="detail-value">
                                    <span class="role-tag" :class="getRoleClass(userdata.userrole)">
                                        {{ getUserRoleLabel(userdata.userrole) }}
                                    </span>
                                </div>
                            </div>

                            <div class="detail-item">
                                <label class="detail-label">Status</label>
                                <div class="detail-value">
                                    <span class="status-tag" :class="{ 'active': userdata.active }">
                                        {{ userdata.active ? 'Active' : 'Inactive' }}
                                    </span>
                                </div>
                            </div>

                            <div v-if="showLocation" class="detail-item">
                                <label class="detail-label">Location(s)</label>
                                <div class="detail-value">
                                    <b-icon icon="geo-alt" class="mr-2"></b-icon>
                                    <div v-if="userdata.multiple_locations_enabled && userdata.location_ids && userdata.location_ids.length">
                                        <div v-for="locId in userdata.location_ids" :key="locId" class="location-badge">
                                            {{ getLocationName(locId) }}
                                        </div>
                                    </div>
                                    <div v-else>
                                        {{ getLocationName(userdata.properties.location_id) }}
                                    </div>
                                </div>
                            </div>

                            <div class="detail-item">
                                <label class="detail-label">Date Added</label>
                                <div class="detail-value">
                                    <b-icon icon="calendar" class="mr-2"></b-icon>
                                    {{ formatDate(userdata.ts) }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Commission Information -->
                    <div v-if="showCommission" class="detail-card">
                        <h5 class="detail-title">
                            <b-icon icon="percent" class="mr-2"></b-icon>
                            Commission Settings
                        </h5>
                        <div class="commission-grid">
                            <div class="commission-item">
                                <label class="commission-label">Sale Commissions (%)</label>
                                <div class="commission-value">
                                    {{ userdata.commission_percentage || 0 }}%
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Delivery Commission (%)</label>
                                <div class="commission-value">
                                    {{ userdata.delivery_commission || 0 }}%
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Setup Commission (%)</label>
                                <div class="commission-value">
                                    {{ userdata.setup_commission || 0 }}%
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Warranty Commission (%)</label>
                                <div class="commission-value">
                                    {{ userdata.warranty_commission || 0 }}%
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Spiff Allowed</label>
                                <div class="commission-value">
                                    <b-icon
                                        :icon="userdata.spiff_allowed ? 'check-circle-fill' : 'x-circle-fill'"
                                        :variant="userdata.spiff_allowed ? 'success' : 'danger'"
                                    ></b-icon>
                                    {{ userdata.spiff_allowed ? 'Yes' : 'No' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ============================================================ -->
                    <!-- NEW: Max Discount Caps (view mode)                            -->
                    <!-- ============================================================ -->
                    <div v-if="showCommission" class="detail-card">
                        <h5 class="detail-title">
                            <b-icon icon="tag" class="mr-2"></b-icon>
                            Max Discount Caps
                        </h5>
                        <div class="commission-grid">
                            <div class="commission-item">
                                <label class="commission-label">Regular</label>
                                <div class="commission-value">
                                    <span class="cap-pill">{{ fmtCap(userdata.max_discount_regular) }}</span>
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Sale</label>
                                <div class="commission-value">
                                    <span class="cap-pill is-sale">{{ fmtCap(userdata.max_discount_sale) }}</span>
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Promo</label>
                                <div class="commission-value">
                                    <span class="cap-pill is-promo">{{ fmtCap(userdata.max_discount_promo) }}</span>
                                </div>
                            </div>

                            <div class="commission-item">
                                <label class="commission-label">Clearance</label>
                                <div class="commission-value">
                                    <span class="cap-pill is-clearance">{{ fmtCap(userdata.max_discount_clearance) }}</span>
                                </div>
                            </div>
                        </div>
                        <small class="text-muted d-block mt-2">
                            <b-icon icon="info-circle" size="sm" class="mr-1"></b-icon>
                            Applied on top of the product-level cap when this user manually discounts items in the POS.
                        </small>
                    </div>
                </div>

                <div class="action-buttons">
                    <b-button @click="triggerEdit" variant="primary" class="edit-btn">
                        <b-icon icon="pencil" class="mr-2"></b-icon>
                        Edit User
                    </b-button>
                    <b-button
                        @click="viewProfile"
                        variant="primary"
                        class="edit-btn"
                        title="View Sales Profile"
                    >
                        <b-icon icon="person-lines-fill"></b-icon> View Profile
                    </b-button>
                </div>
            </div>

            <!-- Edit Mode -->
            <div v-else>
                <div class="edit-header">
                    <h3 class="edit-title">
                        <b-icon icon="pencil" class="mr-2"></b-icon>
                        {{ userdata.id ? 'Edit User' : 'Add New User' }}
                    </h3>
                </div>

                <div class="edit-form">
                    <b-card no-body class="mb-4">
                        <b-card-header class="bg-light">
                            <h6 class="mb-0">Basic Information</h6>
                        </b-card-header>
                        <b-card-body>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="Full Name" label-for="fullname">
                                        <b-form-input
                                            id="fullname"
                                            v-model="userdata.fullname"
                                            placeholder="Enter full name"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>

                                <b-col md="6">
                                    <b-form-group label="Username" label-for="username">
                                        <b-form-input
                                            id="username"
                                            v-model="userdata.username"
                                            placeholder="Username without spaces/special characters"
                                        ></b-form-input>
                                        <small class="text-muted">Should be without spaces and special characters</small>
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="User Role" label-for="userrole">
                                        <b-form-select
                                            id="userrole"
                                            v-model="userdata.userrole"
                                            :options="userroles"
                                            @change="onRoleChange"
                                        ></b-form-select>
                                    </b-form-group>
                                </b-col>

                                <b-col md="6">
                                    <b-form-group label="Status" label-for="status">
                                        <b-form-select
                                            id="status"
                                            v-model="userdata.active"
                                            :options="active_statuses"
                                        ></b-form-select>
                                    </b-form-group>
                                </b-col>
                            </b-row>

                            <!-- Enhanced Location Management Section -->
                            <div v-if="showLocation" class="location-management-section">
                                <div class="location-header d-flex justify-content-between align-items-center mb-3">
                                    <label class="font-weight-bold mb-0">
                                        <b-icon icon="geo-alt" class="mr-2"></b-icon>
                                        Location Management
                                    </label>
                                    <b-form-checkbox
                                        v-model="multiple_locations_enabled"
                                        @change="toggleMultipleLocations"
                                        switch
                                        class="location-toggle"
                                    >
                                        <span class="toggle-label">
                                            {{ multiple_locations_enabled ? 'Multiple Locations' : 'Single Location' }}
                                        </span>
                                    </b-form-checkbox>
                                </div>

                                <!-- Single Location Mode with Enhanced UI -->
                                <div v-if="!multiple_locations_enabled" class="single-location-mode">
                                    <div class="location-select-wrapper">
                                        <b-form-select
                                            v-model="primary_location_id"
                                            :options="locations_data"
                                            @change="updateSingleLocation"
                                            class="location-select"
                                            :state="primary_location_id ? true : null"
                                        >
                                            <option :value="null" disabled>Select a primary location</option>
                                        </b-form-select>
                                        <b-icon
                                            v-if="primary_location_id"
                                            icon="check-circle-fill"
                                            variant="success"
                                            class="location-select-icon"
                                        ></b-icon>
                                    </div>
                                    <small class="text-muted d-block mt-2">
                                        <b-icon icon="info-circle" size="sm"></b-icon>
                                        This user will be associated with this location only
                                    </small>
                                </div>

                                <!-- Enhanced Multiple Locations Mode -->
                                <div v-else class="multiple-locations-mode">
                                    <div class="location-picker-card">
                                        <div class="location-search-wrapper mb-3">
                                            <b-input-group>
                                                <b-input-group-prepend>
                                                    <b-input-group-text>
                                                        <b-icon icon="search"></b-icon>
                                                    </b-input-group-text>
                                                </b-input-group-prepend>
                                                <b-form-input
                                                    v-model="locationSearchQuery"
                                                    placeholder="Search locations..."
                                                    @input="filterLocations"
                                                ></b-form-input>
                                                <b-input-group-append v-if="locationSearchQuery">
                                                    <b-button @click="clearSearch" variant="outline-secondary">
                                                        <b-icon icon="x"></b-icon>
                                                    </b-button>
                                                </b-input-group-append>
                                            </b-input-group>
                                        </div>

                                        <div class="location-grid">
                                            <div class="available-locations-section">
                                                <div class="section-header d-flex justify-content-between align-items-center mb-2">
                                                    <label class="mb-0 font-weight-bold">
                                                        Available Locations
                                                        <span class="text-muted ml-2">({{ filteredLocations.length }})</span>
                                                    </label>
                                                    <b-button
                                                        v-if="filteredLocations.length > 0 && location_ids.length < filteredLocations.length"
                                                        @click="selectAllVisible"
                                                        size="sm"
                                                        variant="link"
                                                        class="p-0"
                                                    >
                                                        Select All
                                                    </b-button>
                                                </div>

                                                <div class="location-list" v-if="filteredLocations.length > 0">
                                                    <div
                                                        v-for="location in filteredLocations"
                                                        :key="location.value"
                                                        :class="['location-item', { 'selected': isLocationSelected(location.value) }]"
                                                        @click="toggleLocation(location.value)"
                                                    >
                                                        <div class="location-item-content">
                                                            <div class="location-info">
                                                                <b-icon
                                                                    :icon="isLocationSelected(location.value) ? 'check-circle-fill' : 'circle'"
                                                                    :variant="isLocationSelected(location.value) ? 'success' : 'secondary'"
                                                                    class="location-check-icon"
                                                                ></b-icon>
                                                                <span class="location-name">{{ location.text }}</span>
                                                            </div>
                                                            <div v-if="isLocationSelected(location.value)" class="location-badge">
                                                                Selected
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div v-else class="empty-locations text-center py-4">
                                                    <b-icon icon="search" font-scale="2" class="text-muted mb-2"></b-icon>
                                                    <p class="text-muted mb-0">No locations found</p>
                                                </div>
                                            </div>

                                            <div class="selected-locations-section">
                                                <div class="section-header d-flex justify-content-between align-items-center mb-2">
                                                    <label class="mb-0 font-weight-bold">
                                                        Selected Locations
                                                        <span class="text-muted ml-2">({{ location_ids.length }})</span>
                                                    </label>
                                                    <b-button
                                                        v-if="location_ids.length > 0"
                                                        @click="clearAllSelected"
                                                        size="sm"
                                                        variant="link"
                                                        class="p-0 text-danger"
                                                    >
                                                        Clear All
                                                    </b-button>
                                                </div>

                                                <div class="selected-locations-container" v-if="location_ids.length > 0">
                                                    <div
                                                        v-for="locId in location_ids"
                                                        :key="locId"
                                                        class="selected-location-item"
                                                    >
                                                        <div class="selected-location-info">
                                                            <b-icon icon="geo-alt-fill" variant="primary" size="sm" class="mr-2"></b-icon>
                                                            <span class="selected-location-name">{{ getLocationName(locId) }}</span>
                                                            <span v-if="locId === primary_location_id" class="primary-badge">Primary</span>
                                                        </div>
                                                        <div class="selected-location-actions">
                                                            <b-button
                                                                v-if="location_ids.length > 1 && locId !== primary_location_id"
                                                                @click="setAsPrimary(locId)"
                                                                size="sm"
                                                                variant="outline-primary"
                                                                class="mr-1"
                                                                title="Set as primary location"
                                                            >
                                                                <b-icon icon="star"></b-icon>
                                                            </b-button>
                                                            <b-button
                                                                @click="removeLocation(locId)"
                                                                size="sm"
                                                                variant="outline-danger"
                                                                title="Remove location"
                                                            >
                                                                <b-icon icon="x"></b-icon>
                                                            </b-button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div v-else class="empty-selected text-center py-4">
                                                    <b-icon icon="geo-alt" font-scale="2" class="text-muted mb-2"></b-icon>
                                                    <p class="text-muted mb-0">No locations selected</p>
                                                    <small class="text-muted">Select locations from the list on the left</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="location_ids.length > 1" class="mt-3">
                                            <b-alert show variant="info" class="small mb-0">
                                                <b-icon icon="info-circle" class="mr-2"></b-icon>
                                                <strong>Multi-Location Access:</strong> This user will have access to all
                                                <strong>{{ location_ids.length }}</strong> selected locations.
                                                <span v-if="primary_location_id">
                                                    <strong>{{ getLocationName(primary_location_id) }}</strong> is set as the primary location for backward compatibility.
                                                </span>
                                            </b-alert>
                                        </div>

                                        <div v-else-if="location_ids.length === 1" class="mt-3">
                                            <b-alert show variant="warning" class="small mb-0">
                                                <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
                                                <strong>Single Location:</strong> The user has access to only one location.
                                                Consider enabling multiple locations mode for broader access.
                                            </b-alert>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>

                    <!-- Commission Settings -->
                    <div v-if="showCommission">
                        <b-card no-body class="mb-4">
                            <b-card-header class="bg-light">
                                <h6 class="mb-0">Commission Settings</h6>
                            </b-card-header>
                            <b-card-body>
                                <b-row>
                                    <b-col md="3">
                                        <b-form-group label="Sale Commission %" label-for="sale-commission">
                                            <b-form-input
                                                id="sale-commission"
                                                v-model="userdata.commission_percentage"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Delivery Commission %" label-for="delivery-commission">
                                            <b-form-input
                                                id="delivery-commission"
                                                v-model="userdata.delivery_commission"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Setup Commission %" label-for="setup-commission">
                                            <b-form-input
                                                id="setup-commission"
                                                v-model="userdata.setup_commission"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Warranty Commission %" label-for="warranty-commission">
                                            <b-form-input
                                                id="warranty-commission"
                                                v-model="userdata.warranty_commission"
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                max="100"
                                            ></b-form-input>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row>
                                    <b-col md="12">
                                        <b-form-checkbox
                                            v-model="userdata.spiff_allowed"
                                            :value="true"
                                            :unchecked-value="false"
                                        >
                                            Allow Spiff Commission
                                        </b-form-checkbox>
                                    </b-col>
                                </b-row>

                                <div class="mt-3">
                                    <b-button
                                        @click="update_user_commissions"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <b-icon icon="check" class="mr-2"></b-icon>
                                        Update Commission Settings
                                    </b-button>
                                </div>
                            </b-card-body>
                        </b-card>

                        <!-- ==================================================== -->
                        <!-- NEW: Max Discount Caps (edit mode)                    -->
                        <!-- ==================================================== -->
                        <b-card no-body class="mb-4">
                            <b-card-header class="bg-light">
                                <h6 class="mb-0">
                                    <b-icon icon="tag" class="mr-2"></b-icon>
                                    Max Discount Caps
                                </h6>
                            </b-card-header>
                            <b-card-body>
                                <p class="text-muted small mb-3">
                                    Percentages (0–100). Leave blank to fall back to the product-level cap.
                                    The POS applies the <strong>smaller</strong> of this user cap and the product cap.
                                </p>

                                <b-row>
                                    <b-col md="3">
                                        <b-form-group label="Regular %" label-for="max-discount-regular">
                                            <b-input-group append="%">
                                                <b-form-input
                                                    id="max-discount-regular"
                                                    v-model.number="userdata.max_discount_regular"
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    max="100"
                                                    placeholder="—"
                                                ></b-form-input>
                                            </b-input-group>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Sale %" label-for="max-discount-sale">
                                            <b-input-group append="%">
                                                <b-form-input
                                                    id="max-discount-sale"
                                                    v-model.number="userdata.max_discount_sale"
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    max="100"
                                                    placeholder="—"
                                                ></b-form-input>
                                            </b-input-group>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Promo %" label-for="max-discount-promo">
                                            <b-input-group append="%">
                                                <b-form-input
                                                    id="max-discount-promo"
                                                    v-model.number="userdata.max_discount_promo"
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    max="100"
                                                    placeholder="—"
                                                ></b-form-input>
                                            </b-input-group>
                                        </b-form-group>
                                    </b-col>

                                    <b-col md="3">
                                        <b-form-group label="Clearance %" label-for="max-discount-clearance">
                                            <b-input-group append="%">
                                                <b-form-input
                                                    id="max-discount-clearance"
                                                    v-model.number="userdata.max_discount_clearance"
                                                    type="number"
                                                    step="0.1"
                                                    min="0"
                                                    max="100"
                                                    placeholder="—"
                                                ></b-form-input>
                                            </b-input-group>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <div class="mt-3">
                                    <b-button
                                        @click="update_user_commissions"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <b-icon icon="check" class="mr-2"></b-icon>
                                        Update Discount Caps
                                    </b-button>
                                </div>
                            </b-card-body>
                        </b-card>
                    </div>

                    <!-- Password Settings -->
                    <b-card no-body class="mb-4">
                        <b-card-header class="bg-light">
                            <div class="d-flex justify-content-between align-items-center">
                                <h6 class="mb-0">Password Settings</h6>
                                <b-button
                                    @click="set_edit_password"
                                    variant="outline-primary"
                                    size="sm"
                                >
                                    {{ edit_password ? 'Cancel' : 'Set Password' }}
                                </b-button>
                            </div>
                        </b-card-header>

                        <b-card-body v-if="edit_password">
                            <b-row>
                                <b-col md="6">
                                    <b-form-group label="New Password" label-for="password1">
                                        <b-input-group>
                                            <b-form-input
                                                id="password1"
                                                v-model="passwords.password_1"
                                                :type="passwordFieldType"
                                                placeholder="Enter new password"
                                            ></b-form-input>
                                            <b-input-group-append>
                                                <b-button @click="togglePasswordType" variant="outline-secondary">
                                                    <b-icon :icon="passwordFieldType === 'password' ? 'eye' : 'eye-slash'"></b-icon>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                    </b-form-group>
                                </b-col>

                                <b-col md="6">
                                    <b-form-group label="Confirm Password" label-for="password2">
                                        <b-form-input
                                            id="password2"
                                            v-model="passwords.password_2"
                                            :type="passwordFieldType"
                                            placeholder="Repeat new password"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-card-body>
                    </b-card>

                    <!-- Action Buttons -->
                    <div class="edit-actions">
                        <b-button
                            @click="userdata.id ? cancelEdit($event) : $emit('cancel')"
                            variant="outline-secondary"
                            class="mr-2"
                        >
                            Cancel
                        </b-button>

                        <b-button
                            @click="userdata.id ? updateUser($event) : addNew($event)"
                            variant="primary"
                        >
                            {{ userdata.id ? 'Update User' : 'Save User' }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Image Viewer Modal -->
        <b-modal id="modal-user-view-image" size="md" hide-footer centered title="User Image">
            <div class="text-center">
                <b-img
                    :src="getUserImage()"
                    fluid
                    alt="Full size user image"
                    style="max-height: 70vh; object-fit: contain; border-radius: 50%;"
                />
                <div class="mt-3">
                    <strong>{{ userdata.fullname || userdata.username || 'User Image' }}</strong>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-user-view-image')">
                    Close
                </b-button>
            </template>
        </b-modal>

        <!-- Image Upload Modal -->
        <b-modal
            id="modal-user-view-upload"
            size="md"
            hide-footer
            centered
            :title="`Upload Image - ${userdata.fullname || userdata.username}`"
            @hidden="resetImageUpload"
        >
            <div class="image-upload-container">
                <div v-if="!userImagePreview" class="upload-area" @click="triggerFileInput">
                    <div class="upload-placeholder">
                        <b-icon icon="cloud-upload" font-scale="3"></b-icon>
                        <div class="mt-2">Click to select an image</div>
                        <small class="text-muted">or drag and drop</small>
                    </div>
                </div>

                <div v-else class="image-preview-container">
                    <img :src="userImagePreview" alt="Preview" class="preview-image" />
                    <div class="preview-actions mt-2">
                        <b-button size="sm" variant="secondary" @click="clearImage">
                            Remove
                        </b-button>
                    </div>
                </div>

                <input
                    ref="fileInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleFileSelect"
                />

                <div class="upload-info mt-3">
                    <small class="text-muted">
                        Supported formats: JPG, PNG, GIF. Max size: 5MB
                    </small>
                </div>

                <div class="mt-4 text-right">
                    <b-button
                        variant="secondary"
                        @click="$bvModal.hide('modal-user-view-upload')"
                        class="mr-2"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="uploadImage"
                        :disabled="!selectedFile || uploadingImage"
                    >
                        {{ uploadingImage ? 'Uploading...' : 'Upload Image' }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Requestor from "@/utilities/Requestor"

    export default {
        name: "UserView",
        props: ["viewdata", "new", "userDetailsProp", "locations"],
        data: function() {
            return {
                packet: {},
                passwordFieldType: "password",
                userDetails: {},
                userdata: { properties: { location_id: null } },
                userproperties: {},
                selectedFile: null,
                userImagePreview: null,
                uploadingImage: false,
                userroles: [
                    { value: "admin", text: "Admin" },
                    { value: "accountmanager", text: "Account Manager" },
                    { value: "officeuser", text: "Office User" },
                    { value: "officemanager", text: "Office Manager" },
                    { value: "locationadmin", text: "Location Admin" },
                    { value: "locationuser", text: "Location User" },
                    { value: "locationstockperson", text: "Location Stock Person" },
                    { value: "salesperson", text: "Sales Person" },
                    { value: "warehouseuser", text: "Warehouse User" },
                    { value: "warehousemanager", text: "Warehouse Manager" },
                    { value: "accountsuser", text: "Accounts User" },
                    { value: "housetruck", text: "House Truck" },
                    { value: "deliverytruck", text: "Delivery Truck" }
                ],
                active_statuses: [
                    { value: true, text: "Active" },
                    { value: false, text: "Inactive" }
                ],
                passwords: {
                    password_1: null,
                    password_2: null,
                },
                edit: false,
                locations_data: [],
                edit_password: false,
                // Multi-location properties
                multiple_locations_enabled: false,
                primary_location_id: null,
                location_ids: [],
                // Enhanced location selection properties
                locationSearchQuery: '',
                filteredLocationsCache: [],
                locationSearchTimeout: null
            };
        },
        computed: {
            filteredLocations() {
                if (!this.locationSearchQuery) {
                    return this.locations_data;
                }
                const query = this.locationSearchQuery.toLowerCase();
                return this.locations_data.filter(loc =>
                    loc.text.toLowerCase().includes(query)
                );
            },

            isAdmin() {
                return this.userDetails && this.userDetails.userrole === 'admin';
            },

            showLocation() {
                const locationRoles = ['locationuser', 'locationadmin', 'salesperson', 'warehouseuser',
                                      'warehousemanager', 'locationstockperson'];
                return locationRoles.includes(this.userdata.userrole);
            },

            showCommission() {
                return !['housetruck', 'deliverytruck'].includes(this.userdata.userrole);
            }
        },
        methods: {
            // ================================================================
            // NEW: format a max-discount cap for display
            // ================================================================
            fmtCap(v) {
                if (v === null || v === undefined || v === '') return '—';
                const n = parseFloat(v);
                if (isNaN(n)) return '—';
                return n.toFixed(1) + '%';
            },

            viewProfile: function() {
                this.$router.push({
                    name: 'UserProfile',
                    params: { username: this.userdata.username }
                });
            },

            // Image Methods
            getUserImage() {
                if (!this.userdata || !this.userdata.properties || !this.userdata.properties.image_url) {
                    return null;
                }

                var imageUrl = this.userdata.properties.image_url;
                if (typeof imageUrl === 'string') {
                    imageUrl = imageUrl.trim();
                }

                if (!imageUrl || imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                    return null;
                }

                if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return 'https://corsproxy.io/?' + encodeURIComponent(imageUrl);
                }

                return imageUrl;
            },

            handleImageError(event) {
                console.warn('Image failed to load');
                event.target.style.display = 'none';
            },

            showFullImage() {
                var imageUrl = this.getUserImage();
                if (imageUrl) {
                    this.$bvModal.show('modal-user-view-image');
                } else {
                    this.$bvToast.toast(
                        "No image available for this user",
                        { title: "Info", autoHideDelay: 2000, variant: "info" }
                    );
                }
            },

            getInitials(name) {
                if (!name) return '?';
                return name.split(' ')
                    .map(function(word) { return word[0]; })
                    .join('')
                    .toUpperCase()
                    .substring(0, 2);
            },

            getRoleClass(role) {
                var roleClasses = {
                    'admin': 'role-admin',
                    'officeuser': 'role-office',
                    'locationadmin': 'role-location',
                    'salesperson': 'role-sales',
                    'accountmanager': 'role-account',
                    'warehouseuser': 'role-warehouse',
                    'default': 'role-default'
                };
                return roleClasses[role] || roleClasses.default;
            },

            getUserRoleLabel(role) {
                var roleObj = this.userroles.find(function(r) {
                    return r.value === role;
                });
                return roleObj ? roleObj.text : role;
            },

            // Enhanced Location Management Methods
            filterLocations() {
                if (this.locationSearchTimeout) {
                    clearTimeout(this.locationSearchTimeout);
                }
                this.locationSearchTimeout = setTimeout(() => {
                    this.locationSearchTimeout = null;
                }, 300);
            },

            clearSearch() {
                this.locationSearchQuery = '';
            },

            isLocationSelected(locationId) {
                return this.location_ids.includes(locationId);
            },

            toggleLocation(locationId) {
                const index = this.location_ids.indexOf(locationId);
                if (index > -1) {
                    this.location_ids.splice(index, 1);
                    if (this.primary_location_id === locationId && this.location_ids.length > 0) {
                        this.setAsPrimary(this.location_ids[0]);
                    } else if (this.location_ids.length === 0) {
                        this.primary_location_id = null;
                    }
                } else {
                    this.location_ids.push(locationId);
                    if (this.location_ids.length === 1) {
                        this.setAsPrimary(locationId);
                    }
                }
                this.updateLocationData();
            },

            selectAllVisible() {
                const visibleIds = this.filteredLocations.map(loc => loc.value);
                const newIds = [...new Set([...this.location_ids, ...visibleIds])];
                this.location_ids = newIds;

                if (!this.primary_location_id && this.location_ids.length > 0) {
                    this.setAsPrimary(this.location_ids[0]);
                }
                this.updateLocationData();
            },

            clearAllSelected() {
                this.location_ids = [];
                this.primary_location_id = null;
                this.updateLocationData();
            },

            setAsPrimary(locationId) {
                if (this.location_ids.includes(locationId)) {
                    this.primary_location_id = locationId;
                    this.updateLocationData();
                }
            },

            updateLocationData() {
                if (!this.userdata.properties) {
                    this.userdata.properties = {};
                }

                if (this.multiple_locations_enabled) {
                    this.userdata.properties.location_ids = this.location_ids;
                    this.userdata.multiple_locations_enabled = true;
                    this.userdata.location_ids = this.location_ids;
                    if (this.primary_location_id) {
                        this.userdata.properties.location_id = this.primary_location_id;
                    }
                } else {
                    this.userdata.properties.location_id = this.primary_location_id;
                    this.userdata.multiple_locations_enabled = false;
                    this.userdata.location_ids = [];
                }
            },

            onRoleChange() {
                if (!this.showLocation) {
                    this.multiple_locations_enabled = false;
                    this.primary_location_id = null;
                    this.location_ids = [];
                }
            },

            toggleMultipleLocations(enabled) {
                if (enabled) {
                    if (this.primary_location_id) {
                        this.location_ids = [parseInt(this.primary_location_id)];
                    } else if (this.userdata.properties && this.userdata.properties.location_id) {
                        this.primary_location_id = parseInt(this.userdata.properties.location_id);
                        this.location_ids = [parseInt(this.userdata.properties.location_id)];
                    } else {
                        this.location_ids = [];
                    }
                } else {
                    if (this.location_ids && this.location_ids.length) {
                        this.primary_location_id = this.location_ids[0];
                    }
                    this.location_ids = [];
                }
                this.multiple_locations_enabled = enabled;
                this.updateLocationData();
            },

            updateSingleLocation(locationId) {
                if (!this.userdata.properties) {
                    this.userdata.properties = {};
                }
                this.userdata.properties.location_id = locationId;
                this.primary_location_id = locationId;
            },

            removeLocation(locationId) {
                const index = this.location_ids.indexOf(locationId);
                if (index > -1) {
                    this.location_ids.splice(index, 1);
                    if (this.primary_location_id == locationId && this.location_ids.length) {
                        this.setAsPrimary(this.location_ids[0]);
                    } else if (this.location_ids.length === 0) {
                        this.primary_location_id = null;
                    }
                    this.updateLocationData();
                }
            },

            // Image Upload Methods
            openImageUpload() {
                this.selectedFile = null;
                this.userImagePreview = null;
                this.$bvModal.show('modal-user-view-upload');
            },

            triggerFileInput() {
                this.$refs.fileInput.click();
            },

            handleFileSelect(event) {
                var file = event.target.files[0];
                if (!file) return;

                var validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                if (!validTypes.includes(file.type)) {
                    this.$bvToast.toast(
                        'Please select a valid image file (JPG, PNG, GIF)',
                        { title: "Invalid File", autoHideDelay: 2000, variant: "warning" }
                    );
                    return;
                }

                if (file.size > 5 * 1024 * 1024) {
                    this.$bvToast.toast(
                        'Image size should be less than 5MB',
                        { title: "File Too Large", autoHideDelay: 2000, variant: "warning" }
                    );
                    return;
                }

                this.selectedFile = file;

                var reader = new FileReader();
                var that = this;
                reader.onload = function(e) {
                    that.userImagePreview = e.target.result;
                };
                reader.readAsDataURL(file);
            },

            clearImage() {
                this.selectedFile = null;
                this.userImagePreview = null;
                this.$refs.fileInput.value = '';
            },

            resetImageUpload() {
                this.selectedFile = null;
                this.userImagePreview = null;
                this.uploadingImage = false;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            },

            uploadImage() {
                var that = this;
                if (!this.selectedFile || !this.userdata) return;

                this.uploadingImage = true;

                this.fileToBase64(this.selectedFile)
                    .then(function(base64Image) {
                        var path = "/api/user/upload_user_image/";
                        var data = {
                            user_id: that.userdata.id,
                            username: that.userdata.username,
                            image_data: base64Image
                        };

                        return new Promise(function(resolve, reject) {
                            Requestor.postSyncP(path, data, that, resolve, reject);
                        });
                    })
                    .then(function(response) {
                        if (response.data.error) {
                            throw new Error(response.data.error);
                        }

                        var updatedUser = response.data.result;
                        that.userdata.properties.image_url = updatedUser.image_url;

                        that.$bvToast.toast(
                            "User image uploaded successfully",
                            { title: "Success", autoHideDelay: 2000, variant: "success" }
                        );

                        that.$bvModal.hide('modal-user-view-upload');
                        that.$emit('image-updated', updatedUser);
                    })
                    .catch(function(error) {
                        console.error('Error uploading user image:', error);
                        that.$bvToast.toast(
                            error.message || "Error uploading image",
                            { title: "Error", autoHideDelay: 2000, variant: "danger" }
                        );
                    })
                    .finally(function() {
                        that.uploadingImage = false;
                    });
            },

            fileToBase64(file) {
                return new Promise(function(resolve, reject) {
                    var reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = function() {
                        resolve(reader.result);
                    };
                    reader.onerror = function(error) {
                        reject(error);
                    };
                });
            },

            // Helper Methods
            getLocationName(locationId) {
                if (!locationId || !this.packet.locationsdict) return 'N/A';
                if (Array.isArray(locationId)) {
                    return locationId.map(id => this.packet.locationsdict[id] || 'N/A').join(', ');
                }
                return this.packet.locationsdict[locationId] || 'N/A';
            },

            formatDate(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (error) {
                    return dateString;
                }
            },

            // Original Methods
            update_user_commissions: function(e) {
                if (e) {
                    e.preventDefault();
                }

                var data = this.userdata;
                var path = "/api/user/update_user_commissions/";
                var that = this;

                var success = function(response) {
                    if (response.data) {
                        if (response.data.error) {
                            that.$bvToast.toast(
                                response.data.error,
                                { title: "Error", autoHideDelay: 1500, variant: "danger" }
                            );
                        } else {
                            that.$bvToast.toast(
                                "User commissions updated",
                                { title: "success", autoHideDelay: 1500, variant: "success" }
                            );
                        }
                    }
                };

                var failure = function(error) {
                    if (error) {
                        that.$bvToast.toast(
                            "Error updating user commissions. Please try again.",
                            { title: "Error", autoHideDelay: 1500, variant: "danger" }
                        );
                    }
                };

                Requestor.postSyncP(path, data, this, success, failure);
            },

            set_edit_password: function() {
                this.edit_password = !this.edit_password;
            },

            togglePasswordType: function(e) {
                if (e) e.preventDefault();
                this.passwordFieldType = (this.passwordFieldType === 'password') ? 'text' : 'password';
            },

            cancelEdit: function(e) {
                if (e) e.preventDefault();
                this.userdata = JSON.parse(JSON.stringify(this.viewdata));
                this.initializeLocationData();
                this.edit = false;
            },

            updateUser: function(e) {
                if (e) {
                    e.preventDefault();
                }

                var errors = this.validate("update");
                if (errors.length > 0) {
                    this.$bvToast.toast(
                        errors[0],
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }

                var that = this;
                var path = "/api/user/update/";
                var userdata = JSON.parse(JSON.stringify(this.userdata));

                if (this.showLocation) {
                    if (!userdata.properties) {
                        userdata.properties = {};
                    }

                    if (this.multiple_locations_enabled) {
                        userdata.properties.location_ids = this.location_ids;
                        userdata.properties.multiple_locations_enabled = true;
                        userdata.multiple_locations_enabled = true;
                        userdata.location_ids = this.location_ids;
                        if (this.location_ids && this.location_ids.length) {
                            userdata.properties.location_id = this.location_ids[0];
                        }
                    } else {
                        userdata.properties.multiple_locations_enabled = false;
                        userdata.multiple_locations_enabled = false;
                        userdata.location_ids = [];
                        if (this.primary_location_id) {
                            userdata.properties.location_id = this.primary_location_id;
                        }
                    }
                }

                if (this.edit_password) {
                    userdata.password = this.passwords.password_1;
                }

                var data = { "userdata": userdata, "userrole": this.userDetails.userrole };

                var success = function(response) {
                    if (response.data) {
                        if (response.data.error) {
                            that.$bvToast.toast(
                                response.data.error,
                                { title: "Error", autoHideDelay: 1500, variant: "danger" }
                            );
                            that.userdata = JSON.parse(JSON.stringify(that.viewdata));
                        } else {
                            that.$bvToast.toast(
                                "User data updated",
                                { title: "success", autoHideDelay: 1500, variant: "success" }
                            );
                            that.userdata = response.data.result;
                            that.initializeLocationData();
                            that.$emit('updated', that.userdata);
                        }
                    }
                    that.edit = false;
                    that.edit_password = false;
                    that.passwords = { password_1: null, password_2: null };
                };

                var failure = function(error) {
                    if (error) {
                        that.$bvToast.toast(
                            "Error updating user data. Please try again.",
                            { title: "Error", autoHideDelay: 1500, variant: "danger" }
                        );
                    }
                    that.userdata = JSON.parse(JSON.stringify(that.viewdata));
                    that.edit = false;
                };

                Requestor.postSyncP(path, data, this, success, failure);
            },

            triggerEdit: function(e) {
                if (e) e.preventDefault();
                this.initializeLocationData();
                this.edit = true;
            },

            initializeLocationData() {
                if (this.userdata && this.userdata.properties) {
                    this.multiple_locations_enabled = this.userdata.properties.multiple_locations_enabled || false;

                    if (this.userdata.properties.location_ids && Array.isArray(this.userdata.properties.location_ids)) {
                        this.location_ids = [...this.userdata.properties.location_ids];
                    } else if (this.userdata.location_ids && Array.isArray(this.userdata.location_ids)) {
                        this.location_ids = [...this.userdata.location_ids];
                    } else {
                        this.location_ids = [];
                    }

                    if (this.userdata.properties.location_id) {
                        this.primary_location_id = parseInt(this.userdata.properties.location_id);
                    } else if (this.location_ids && this.location_ids.length) {
                        this.primary_location_id = this.location_ids[0];
                    } else {
                        this.primary_location_id = null;
                    }
                } else {
                    this.multiple_locations_enabled = false;
                    this.location_ids = [];
                    this.primary_location_id = null;
                }

                this.locationSearchQuery = '';
            },

            validate: function(action) {
                var errors = [];

                if (action === "add" || action === "update") {
                    if (this.edit_password) {
                        if ((this.passwords.password_1 !== this.passwords.password_2) || (!this.passwords.password_1)) {
                            errors.push("Both passwords should match and should be non-empty values.");
                        }
                    }
                }

                if (!this.userdata.username) {
                    errors.push("Please enter a valid username");
                }

                if (!this.userdata.userrole) {
                    errors.push("Please select a user role");
                }

                if (isNaN(parseFloat(this.userdata.commission_percentage))) {
                    this.userdata.commission_percentage = 1.0;
                }

                if (this.showLocation) {
                    if (this.multiple_locations_enabled) {
                        if (!this.location_ids || this.location_ids.length === 0) {
                            errors.push("Please specify at least one location for the user.");
                        }
                    } else {
                        if (!this.primary_location_id) {
                            errors.push("Please specify location of the user.");
                        }
                    }
                }

                return errors;
            },

            addNew: function(e) {
                e.preventDefault();
                var errors = this.validate("add");
                if (errors.length > 0) {
                    this.$bvToast.toast(
                        errors[0],
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                    return;
                }

                var that = this;
                var userdata = JSON.parse(JSON.stringify(this.userdata));
                userdata.username = userdata.username.toLowerCase();

                if (this.showLocation) {
                    if (!userdata.properties) {
                        userdata.properties = {};
                    }

                    if (this.multiple_locations_enabled) {
                        userdata.properties.location_ids = this.location_ids;
                        userdata.properties.multiple_locations_enabled = true;
                        userdata.multiple_locations_enabled = true;
                        userdata.location_ids = this.location_ids;
                        if (this.location_ids && this.location_ids.length) {
                            userdata.properties.location_id = this.location_ids[0];
                        }
                    } else {
                        userdata.properties.multiple_locations_enabled = false;
                        userdata.multiple_locations_enabled = false;
                        userdata.location_ids = [];
                        if (this.primary_location_id) {
                            userdata.properties.location_id = this.primary_location_id;
                        }
                    }
                }

                if (this.userproperties) {
                    if (!userdata.properties) {
                        userdata.properties = {};
                    }
                    for (var key in this.userproperties) {
                        userdata.properties[key] = this.userproperties[key];
                    }
                }

                if (this.edit_password) {
                    userdata.password = this.passwords.password_1;
                }

                var path = "/api/user/add/";
                var data = userdata;

                var success = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "danger" }
                        );
                    } else {
                        that.userdata = response.data.result;
                        that.edit = false;
                        that.edit_password = false;
                        that.passwords = { password_1: null, password_2: null };

                        that.$bvToast.toast(
                            "Added new user.",
                            { title: "Success", autoHideDelay: 1500, variant: "success" }
                        );
                        that.$emit('added', that.userdata);
                    }
                };

                var failure = function(error) {
                    that.$bvToast.toast(
                        "Error adding new user. Please try again.",
                        { title: "Error", autoHideDelay: 1500, variant: "danger" }
                    );
                };

                Requestor.postSyncP(path, data, this, success, failure);
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            this.userDetails = this.userDetailsProp || {};
            this.userdata = JSON.parse(JSON.stringify(this.viewdata));

            if (this.packet.locations) {
                this.locations_data = this.packet.locations.filter(function(d) {
                    return d.text !== "All";
                }).map(function(loc) {
                    return { value: loc.id || loc.value, text: loc.text || loc.name };
                });
            } else if (this.locations) {
                this.locations_data = this.locations.map(function(loc) {
                    return { value: loc.id, text: loc.name };
                });
            }

            this.initializeLocationData();

            this.new = this.new || false;
            if (this.new) {
                this.edit = true;
            }
        }
    };
</script>

<style scoped>
.user-view-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.modern-user-card {
    background: white;
    border-radius: 10px;
    padding: 0;
}

/* Header Section */
.user-header {
    display: flex;
    align-items: center;
    padding: 25px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px 10px 0 0;
}

.user-avatar-section {
    flex-shrink: 0;
    margin-right: 25px;
    text-align: center;
}

.avatar-wrapper {
    cursor: pointer;
    margin-bottom: 10px;
}

.avatar-image-container {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    overflow: hidden;
    border: 3px solid rgba(255, 255, 255, 0.3);
}

.user-avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    color: white;
}

.avatar-image-container:hover .avatar-overlay {
    opacity: 1;
}

.avatar-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1.5rem;
    border: 3px solid rgba(255, 255, 255, 0.3);
}

.no-image-text {
    font-size: 0.8rem;
    margin-top: 5px;
    opacity: 0.8;
}

.upload-btn {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
}

.upload-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: white;
}

.user-info-section {
    flex: 1;
}

.user-name {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 5px;
}

.user-username {
    font-size: 1rem;
    opacity: 0.9;
    margin-bottom: 10px;
}

.user-role-badge {
    display: inline-block;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 10px;
}

.user-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
}

.status-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #cbd5e0;
}

.status-dot.active {
    background: #48bb78;
}

/* Details Section */
.user-details-section {
    padding: 25px;
}

.detail-card {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    border: 1px solid #e2e8f0;
}

.detail-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
}

.detail-item {
    display: flex;
    flex-direction: column;
}

.detail-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #718096;
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.detail-value {
    font-size: 1rem;
    color: #2d3748;
    font-weight: 500;
}

.location-badge {
    display: inline-block;
    background: #e2e8f0;
    padding: 2px 8px;
    border-radius: 12px;
    margin: 2px;
    font-size: 0.85rem;
}

.role-tag {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
}

.role-admin {
    background: #fed7d7;
    color: #c53030;
}

.role-office {
    background: #c6f6d5;
    color: #276749;
}

.role-location {
    background: #bee3f8;
    color: #2c5282;
}

.role-sales {
    background: #e9d8fd;
    color: #553c9a;
}

.role-account {
    background: #feebc8;
    color: #c05621;
}

.role-warehouse {
    background: #cbd5e0;
    color: #4a5568;
}

.role-default {
    background: #e2e8f0;
    color: #4a5568;
}

.status-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 500;
}

.status-tag::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #cbd5e0;
}

.status-tag.active::before {
    background: #48bb78;
}

/* Commission Section */
.commission-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
}

.commission-item {
    display: flex;
    flex-direction: column;
}

.commission-label {
    font-size: 0.85rem;
    color: #718096;
    margin-bottom: 5px;
}

.commission-value {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2d3748;
}

/* NEW: Cap pill styling for view mode */
.cap-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 99px;
    background: #EDF2F7;
    color: #4A5568;
    border: 1px solid #E2E8F0;
    font-size: 0.95rem;
    font-weight: 700;
}

.cap-pill.is-sale {
    background: #E2F2F0;
    color: #0B5552;
    border-color: #BFE1DD;
}

.cap-pill.is-promo {
    background: #FFF1E4;
    color: #B5520B;
    border-color: #F5CFAE;
}

.cap-pill.is-clearance {
    background: #FCEBE8;
    color: #8E2A21;
    border-color: #F3C9C2;
}

/* Edit Mode */
.edit-header {
    padding: 25px 25px 0;
}

.edit-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
}

.edit-form {
    padding: 25px;
}

/* Enhanced Location Management Styles */
.location-management-section {
    margin-top: 20px;
    padding: 20px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.location-header {
    border-bottom: 2px solid #e2e8f0;
    padding-bottom: 10px;
    margin-bottom: 20px;
}

.location-toggle .toggle-label {
    font-weight: 500;
}

/* Single Location Mode */
.single-location-mode {
    position: relative;
}

.location-select-wrapper {
    position: relative;
}

.location-select {
    padding-right: 35px;
}

.location-select-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

/* Multiple Locations Mode */
.multiple-locations-mode {
    margin-top: 15px;
}

.location-picker-card {
    background: white;
    border-radius: 8px;
    overflow: hidden;
}

.location-search-wrapper {
    background: white;
    padding: 15px;
    border-bottom: 1px solid #e2e8f0;
}

.location-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    min-height: 400px;
}

.available-locations-section,
.selected-locations-section {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.section-header {
    padding: 12px 15px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.location-list {
    flex: 1;
    overflow-y: auto;
    max-height: 350px;
}

.location-item {
    padding: 12px 15px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: 1px solid #f1f5f9;
}

.location-item:hover {
    background: #f8fafc;
    transform: translateX(2px);
}

.location-item.selected {
    background: linear-gradient(90deg, #f0f9ff 0%, #ffffff 100%);
    border-left: 3px solid #667eea;
}

.location-item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.location-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.location-check-icon {
    font-size: 1.1rem;
    transition: all 0.2s ease;
}

.location-name {
    font-size: 0.95rem;
    font-weight: 500;
    color: #2d3748;
}

.location-badge {
    background: #667eea;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.selected-locations-container {
    flex: 1;
    overflow-y: auto;
    max-height: 350px;
    padding: 10px;
}

.selected-location-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin-bottom: 8px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
}

.selected-location-item:hover {
    background: #f1f5f9;
    transform: translateX(2px);
}

.selected-location-info {
    display: flex;
    align-items: center;
    flex: 1;
}

.selected-location-name {
    font-size: 0.9rem;
    font-weight: 500;
    color: #2d3748;
}

.primary-badge {
    background: #fbbf24;
    color: #92400e;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    margin-left: 8px;
}

.selected-location-actions {
    display: flex;
    gap: 5px;
}

.selected-location-actions .btn {
    padding: 2px 6px;
    font-size: 0.75rem;
}

.empty-locations,
.empty-selected {
    padding: 40px 20px;
    text-align: center;
    color: #94a3b8;
}

.empty-selected {
    padding: 30px 20px;
}

/* Action Buttons */
.action-buttons {
    padding: 0 25px 25px;
    text-align: right;
}

.edit-btn {
    padding: 10px 20px;
    font-weight: 600;
    border-radius: 8px;
}

.edit-actions {
    padding: 20px 0 0;
    text-align: right;
    border-top: 1px solid #e2e8f0;
}

/* Image Upload Styles */
.image-upload-container {
    padding: 15px;
}

.upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 50%;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
    width: 200px;
    height: 200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-area:hover {
    border-color: #007bff;
    background-color: #e9ecef;
}

.upload-placeholder {
    color: #6c757d;
}

.preview-image {
    border-radius: 50%;
    border: 1px solid #dee2e6;
    object-fit: cover;
    max-height: 200px;
    display: block;
    margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
    .user-header {
        flex-direction: column;
        text-align: center;
        padding: 20px;
    }

    .user-avatar-section {
        margin-right: 0;
        margin-bottom: 20px;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .commission-grid {
        grid-template-columns: 1fr;
    }

    .edit-form .row > [class*="col-"] {
        margin-bottom: 15px;
    }

    .location-grid {
        grid-template-columns: 1fr;
        gap: 15px;
    }

    .available-locations-section,
    .selected-locations-section {
        max-height: 300px;
    }

    .location-list,
    .selected-locations-container {
        max-height: 250px;
    }

    .location-management-section {
        padding: 15px;
    }

    .location-item {
        padding: 10px 12px;
    }

    .selected-location-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .selected-location-actions {
        align-self: flex-end;
    }
}

@media (max-width: 480px) {
    .user-name {
        font-size: 1.5rem;
    }

    .detail-card {
        padding: 15px;
    }

    .edit-actions {
        text-align: center;
    }

    .edit-actions .btn {
        width: 100%;
        margin-bottom: 10px;
    }

    .edit-actions .mr-2 {
        margin-right: 0 !important;
    }

    .location-header {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 10px;
    }

    .location-header .location-toggle {
        align-self: flex-start;
    }

    .location-search-wrapper {
        padding: 10px;
    }

    .location-item-content {
        font-size: 0.85rem;
    }
}

/* Animation */
.location-item,
.selected-location-item {
    animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Scrollbar Styling */
.location-list::-webkit-scrollbar,
.selected-locations-container::-webkit-scrollbar {
    width: 6px;
}

.location-list::-webkit-scrollbar-track,
.selected-locations-container::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.location-list::-webkit-scrollbar-thumb,
.selected-locations-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
}

.location-list::-webkit-scrollbar-thumb:hover,
.selected-locations-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>


// WEBPACK FOOTER //
// src/components/importables/UserView.vue